<template>
    <div class="content-block dx-card">
        <h2>User Metric Reporting</h2>
    </div>

    <div class="content-block dx-card" id="dataDiv">
        <DxDataGrid class="dx-card wide-card" :data-source="userMetrics" :key-expr="userId" :hover-state-enabled="true"
            :selection="{ mode: 'single' }" :show-borders="true" :columns-auto-width="true" :column-min-width="50"
            :allow-column-resizing="true" :column-resizing-mode="columnResize" :allow-column-reordering="true"
            :filter-builder="filterBuilder" :filter-value="filterValue" :style="{ 'max-height': datagridHeight }"
            no-data-text="No data." @initialized="saveGridInstance" @exporting="onExporting">
            <DxLoadPanel :enabled="true" />
            <DxColumnChooser :enabled="true" mode="select" />
            <DxFilterRow :visible="true" />
            <DxHeaderFilter :visible="true" />
            <DxScrolling mode="standard" />
            <DxExport :enabled="true" />
            <DxColumnFixing :enabled="true" />
            <DxPager :show-info="true" :show-page-size-selector="true" :allowed-page-sizes="[20, 50, 100]"
                :show-navigation-buttons="true" />

            <DxToolbar>
                <DxItem location="after" name="columnChooserButton" />
                <DxItem location="after" template="refreshRecordTemplate" />
                <DxItem location="after" template="removeFilterRecordTemplate" />
                <DxItem location="after" name="exportButton" />
            </DxToolbar>

            <template #refreshRecordTemplate>
                <DxButton icon="refresh" @click="refreshData" />
            </template>
            <template #removeFilterRecordTemplate>
                <DxButton icon="filter" @click="removeFilter" />
            </template>

            <DxColumn data-field="userName" caption="User Name" />
            <DxColumn data-field="lastLogin" caption="Last Login" data-type="datetime"
                format="MMMM dd, yyyy hh:mm:ss a" />
            <DxColumn data-field="lastActivity" caption="Last Activity" data-type="datetime"
                format="MMMM dd, yyyy hh:mm:ss a" />
            <DxColumn data-field="loginsLast30Days" caption="Logins Last 30 Days" data-type="number" :visible="false" />
            <DxColumn data-field="loginsLast90Days" caption="Logins Last 90 Days" data-type="number" :visible="false" />
            <DxColumn data-field="loginsLast365Days" caption="Logins Last 365 Days" data-type="number"
                :visible="false" />
            <DxColumn data-field="avgApiCallsPerDay" caption="Avg API Calls/Day" data-type="number"
                :format="{ type: 'fixedPoint', precision: 2 }" />
            <DxColumn data-field="numberOfSessionsToday" caption="Sessions Today" data-type="number" />
            <DxColumn data-field="lastSessionLength" caption="Last Session Length" :visible="false" />
            <DxColumn data-field="averageNumberOfSessionsPerDay" caption="Avg Sessions/Day" data-type="number"
                :format="{ type: 'fixedPoint', precision: 2 }" :visible="false" />
            <DxColumn data-field="averageNumberOfSessionsPerMonth" caption="Avg Sessions/Month" data-type="number"
                :format="{ type: 'fixedPoint', precision: 2 }" :visible="false" />
            <DxColumn data-field="averageLengthOfSessionsPerDay" caption="Avg Session Length/Day" :visible="false" />
            <DxColumn data-field="averageLengthOfSessionsPerMonth" caption="Avg Session Length/Month"
                :visible="false" />
        </DxDataGrid>
    </div>

    <DxLoadPanel v-model:visible="loadingVisible" :message="Loading" :show-indicator="true" :show-pane="true"
        :shading="true" :hide-on-outside-click="false" :on-shown="onLoadPanelShown" :on-hidden="onLoadPanelHidden"
        shading-color="rgba(0,0,0,0.4)" />
</template>

<script>
import {
    DxDataGrid,
    DxColumn,
    DxHeaderFilter,
    DxFilterRow,
    DxScrolling,
    DxColumnChooser,
    DxColumnFixing,
    DxPager,
    DxItem,
    DxToolbar,
    DxExport,
} from "devextreme-vue/data-grid";
import { DxButton } from "devextreme-vue/button";
import { DxLoadPanel } from "devextreme-vue/load-panel";

export default {
    components: {
        DxDataGrid,
        DxColumn,
        DxHeaderFilter,
        DxFilterRow,
        DxScrolling,
        DxColumnChooser,
        DxLoadPanel,
        DxButton,
        DxToolbar,
        DxItem,
        DxExport,
        DxColumnFixing,
        DxPager,
    },
    created() {
        this.loadPageData();
    },
    mounted() {
        window.addEventListener("resize", this.setDimensions);
        this.setDimensions();
    },
    unmounted() {
        window.removeEventListener("resize", this.setDimensions);
    },
    data() {
        return {
            columnResize: "widget",
            dataGridInstance: null,
            loadingVisible: false,
            isLoadPanelVisible: false,
            datagridHeight: "40vh",
            currentArea: null,
            userMetrics: [],
        };
    },
    computed: {},
    methods: {
        onLoadPanelShown() {
            setTimeout(() => {
                this.loadingVisible = false;
            }, 10000);
        },
        onLoadPanelHidden() { },
        saveGridInstance: function (e) {
            this.dataGridInstance = e.component;
        },
        setDimensions() {
            const h = document.documentElement.clientHeight;
            this.datagridHeight = h - 250 + "px";
        },
        removeFilter() {
            this.dataGridInstance.clearFilter();
            this.dataGridInstance.clearSorting();
        },
        refreshData() {
            this.loadPageData();
        },
        async loadPageData(refresh = false) {
            this.loadingVisible = true;
            try {
                await this.$store.dispatch("userMetrics/loadUserMetrics", {
                    forceRefresh: refresh,
                });
                this.userMetrics = this.$store.getters["userMetrics/getUserMetrics"];
            } catch (error) {
                this.error = error.message || "Unexpected Error.";
            }
            this.loadingVisible = false;
        },
        formatDate(date) {
            if (!date) return "";
            const formattedDate = new Date(date);

            return formattedDate instanceof Date && !isNaN(formattedDate)
                ? formattedDate.toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                })
                : "";
        },
        onExporting(e) {
            e.fileName = `UserMetricsDataExport_${new Date().toISOString().slice(0, 10)}`;
        },
    },
};
</script>

<style scoped>
h2 {
    margin-left: 1em;
}
</style>
